/**
 * useFilmComps Hook
 * Manages film comps data fetching, state management, and operations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import compsAPI from '../lib/services/compsAPI';
import { validateFilmIdInput, validateCompTypeFilter, validatePaginationParams } from '../lib/utils/compValidators';
import { formatErrorMessage } from '../lib/utils/compFormatters';
import { getSampleFilmComps } from '../data/sampleFilmComps';

/**
 * Custom hook for managing film comps data
 * @param {string} filmId - IMDB ID of the film
 * @param {Object} options - Hook options
 * @param {string} options.type - Filter by comp type ('modeled', 'greenlight', 'all')
 * @param {number} options.limit - Maximum number of comps to return
 * @param {number} options.offset - Number of comps to skip
 * @param {boolean} options.autoFetch - Whether to automatically fetch data on mount
 * @returns {Object} - Hook state and methods
 */
export const useFilmComps = (filmId, options = {}) => {
  const {
    type = 'all',
    limit = 50,
    offset = 0,
    autoFetch = true,
    filmTitle: providedFilmTitle
  } = options;

  // State management
  // NOTE: Modeled comps have been temporarily removed.
  // They may be re-added in the future when the model improves.
  const [comps, setComps] = useState({
    modeled: [], // Kept for API compatibility but not used
    greenlight: [],
    totalCount: 0,
    modeledCount: 0, // Kept for API compatibility but not used
    greenlightCount: 0,
    loading: false,
    error: null,
    lastFetched: null,
    metadata: null
  });

  // Refs for managing async operations
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    mountedRef.current = false;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  /**
   * Fetch film comps data
   * @param {Object} fetchOptions - Options for fetching
   * @returns {Promise<void>}
   */
  const fetchComps = useCallback(async (fetchOptions = {}) => {
    // Validate film ID
    const filmIdValidation = validateFilmIdInput(filmId);
    if (!filmIdValidation.isValid) {
      if (mountedRef.current) {
        setComps(prev => ({
          ...prev,
          loading: false,
          error: filmIdValidation.error
        }));
      }
      return;
    }

    // Validate comp type filter
    const typeValidation = validateCompTypeFilter(type);
    if (!typeValidation.isValid) {
      if (mountedRef.current) {
        setComps(prev => ({
          ...prev,
          loading: false,
          error: typeValidation.error
        }));
      }
      return;
    }

    // Validate pagination params
    const paginationValidation = validatePaginationParams({ limit, offset });
    if (!paginationValidation.isValid) {
      if (mountedRef.current) {
        setComps(prev => ({
          ...prev,
          loading: false,
          error: paginationValidation.error
        }));
      }
      return;
    }

    // Abort previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    // Set loading state
    if (mountedRef.current) {
      setComps(prev => ({
        ...prev,
        loading: true,
        error: null
      }));
    }

    try {
      const requestOptions = {
        type: fetchOptions.type || type,
        limit: fetchOptions.limit || limit,
        offset: fetchOptions.offset || offset
      };

      const response = await compsAPI.getFilmComps(filmId, requestOptions);

      if (!mountedRef.current) return;

      if (response.success && response.data) {
        const { data } = response;
        // Only use greenlight comps - filter out modeled comps
        const greenlightComps = data.greenlightComps || [];
        setComps({
          modeled: [], // Not used - kept for API compatibility
          greenlight: greenlightComps,
          totalCount: greenlightComps.length,
          modeledCount: 0, // Not used - kept for API compatibility
          greenlightCount: greenlightComps.length,
          loading: false,
          error: null,
          lastFetched: new Date().toISOString(),
          metadata: {
            ...(data.metadata || {}),
            isSample: false
          }
        });
      } else {
        setComps(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to fetch film comps'
        }));
      }
    } catch (error) {
      if (!mountedRef.current) return;

      // Don't set error if request was aborted
      if (error.name === 'AbortError') {
        return;
      }

      // Log API errors for debugging
      console.error('❌ Film Comps API Error:', error.message);

      try {
        const fallbackTitle = fetchOptions.filmTitle || providedFilmTitle || 'Sample Film';
        const sample = getSampleFilmComps(filmId, fallbackTitle);

        console.warn('⚠️ Using sample film comps data as fallback');

        // Only use greenlight comps from sample data
        const greenlightComps = sample.greenlightComps || [];
        setComps({
          modeled: [], // Not used
          greenlight: greenlightComps,
          totalCount: greenlightComps.length,
          modeledCount: 0, // Not used
          greenlightCount: greenlightComps.length,
          loading: false,
          error: null,
          lastFetched: new Date().toISOString(),
          metadata: {
            ...(sample.metadata || {}),
            isSample: true
          }
        });
      } catch (fallbackError) {
        const errorMessage = formatErrorMessage(error);
        console.error('❌ Failed to build sample film comps fallback:', fallbackError);
        setComps(prev => ({
          ...prev,
          loading: false,
          error: errorMessage
        }));
      }
    } finally {
      abortControllerRef.current = null;
    }
  }, [filmId, type, limit, offset, providedFilmTitle]);

  /**
   * Assign a new greenlight comp
   * @param {Object} assignment - Assignment data
   * @returns {Promise<Object>} - Assignment result
   */
  const assignComp = useCallback(async (assignment) => {
    try {
      const response = await compsAPI.assignComp(assignment);
      
      if (response.success) {
        // Refresh comps data after successful assignment
        await fetchComps();
        return response;
      } else {
        throw new Error('Failed to assign comp');
      }
    } catch (error) {
      const errorMessage = formatErrorMessage(error);
      throw new Error(errorMessage);
    }
  }, [fetchComps]);

  /**
   * Update an existing greenlight comp
   * @param {string} compId - ID of the comp to update
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} - Update result
   */
  const updateComp = useCallback(async (compId, updates) => {
    try {
      const response = await compsAPI.updateComp(compId, updates);
      
      if (response.success) {
        // Refresh comps data after successful update
        await fetchComps();
        return response;
      } else {
        throw new Error('Failed to update comp');
      }
    } catch (error) {
      const errorMessage = formatErrorMessage(error);
      throw new Error(errorMessage);
    }
  }, [fetchComps]);

  /**
   * Delete a greenlight comp
   * @param {string} compId - ID of the comp to delete
   * @returns {Promise<Object>} - Delete result
   */
  const deleteComp = useCallback(async (compId) => {
    try {
      const response = await compsAPI.deleteComp(compId);
      
      if (response.success) {
        // Refresh comps data after successful deletion
        await fetchComps();
        return response;
      } else {
        throw new Error('Failed to delete comp');
      }
    } catch (error) {
      const errorMessage = formatErrorMessage(error);
      throw new Error(errorMessage);
    }
  }, [fetchComps]);

  /**
   * Bulk assign comps
   * @param {Array} assignments - Array of assignments
   * @param {string} assignedBy - User ID of the assigner
   * @returns {Promise<Object>} - Bulk assignment result
   */
  const bulkAssign = useCallback(async (assignments, assignedBy) => {
    try {
      const response = await compsAPI.bulkAssign(assignments, assignedBy);
      
      if (response.success) {
        // Refresh comps data after successful bulk assignment
        await fetchComps();
        return response;
      } else {
        throw new Error('Failed to bulk assign comps');
      }
    } catch (error) {
      const errorMessage = formatErrorMessage(error);
      throw new Error(errorMessage);
    }
  }, [fetchComps]);

  /**
   * Refresh comps data
   * @param {Object} refreshOptions - Options for refresh
   * @returns {Promise<void>}
   */
  const refreshComps = useCallback(async (refreshOptions = {}) => {
    await fetchComps(refreshOptions);
  }, [fetchComps]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    if (mountedRef.current) {
      setComps(prev => ({
        ...prev,
        error: null
      }));
    }
  }, []);

  /**
   * Get filtered comps based on current type filter
   * @returns {Object} - Filtered comps
   */
  const getFilteredComps = useCallback(() => {
    switch (type) {
      case 'modeled':
        return {
          modeled: comps.modeled,
          greenlight: [],
          totalCount: comps.modeledCount,
          modeledCount: comps.modeledCount,
          greenlightCount: 0
        };
      case 'greenlight':
        return {
          modeled: [],
          greenlight: comps.greenlight,
          totalCount: comps.greenlightCount,
          modeledCount: 0,
          greenlightCount: comps.greenlightCount
        };
      default:
        return {
          modeled: comps.modeled,
          greenlight: comps.greenlight,
          totalCount: comps.totalCount,
          modeledCount: comps.modeledCount,
          greenlightCount: comps.greenlightCount
        };
    }
  }, [type, comps]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (autoFetch && filmId) {
      fetchComps();
    }
  }, [filmId, type, limit, offset, autoFetch, fetchComps]);

  // Return hook interface
  return {
    // State
    comps: getFilteredComps(),
    loading: comps.loading,
    error: comps.error,
    lastFetched: comps.lastFetched,
    
    // Methods
    fetchComps,
    refreshComps,
    assignComp,
    updateComp,
    deleteComp,
    bulkAssign,
    clearError,
    
    // Utilities
    isLoaded: !comps.loading && !comps.error && comps.lastFetched !== null,
    hasError: !!comps.error,
    isEmpty: !comps.loading && !comps.error && comps.totalCount === 0,
    
    // Metadata
    totalCount: comps.totalCount,
    modeledCount: comps.modeledCount,
    greenlightCount: comps.greenlightCount,
    metadata: comps.metadata
  };
};

export default useFilmComps;
